/**
 * The naive multi-model mean, and the three rules that stop it lying.
 *
 * **NBM is excluded and gets its own column.** NBM *is* a blend, built
 * properly, with bias correction and a great deal more skill than the mean of
 * five deterministic runs. Averaging it into a naive mean double-counts it and
 * makes the naive column look better than it is, so it sits beside the blend
 * rather than inside it, labelled as the official one.
 *
 * **Fewer than two contributors is not a blend.** One model's value copied
 * into a column called "Blend" is the same number twice with a second name on
 * it, which reads as agreement.
 *
 * **Every cell prints its own *n*.** The membership changes from hour to hour
 * as GFS drops to 3-hourly and NAM runs out, so the mean of a *different
 * subset* at each hour is not a consistent product — and printing n is what
 * stops it pretending to be one.
 *
 * It lives in the frontend rather than the server because hiding a model in
 * the legend has to recompute it, and a round trip per checkbox would be
 * absurd.
 */

import type { CompareSeries } from '../api/compare'

/** The official blend, which is a product rather than a mean of products. */
export const OFFICIAL_BLEND_ID = 'nbm'

/** Below this many contributing models there is no blend at all. */
export const MIN_CONTRIBUTORS = 2

export interface BlendCell {
  value: number
  /** How many models contributed *at this instant*. Always printed. */
  n: number
  /** Which ones, so a tooltip can say and a test can assert. */
  ids: string[]
}

/**
 * Which series a naive blend may average: the deterministic models, minus
 * anything the reader has hidden.
 *
 * A hidden series counts in `unavailable` and not in the blend — forgetting
 * that makes the blend claim more agreement than it has.
 */
export function contributors(
  series: readonly CompareSeries[],
  hidden: ReadonlySet<string> = new Set(),
): CompareSeries[] {
  return series.filter((s) => s.kind === 'model' && s.id !== OFFICIAL_BLEND_ID && !hidden.has(s.id))
}

/**
 * The blend at each instant in `stamps`, or null where it cannot exist.
 *
 * Nothing is interpolated to make a cell exist: a model with no step at that
 * instant simply does not contribute, which is the same rule the table's
 * dashes follow.
 */
export function blend(
  series: readonly CompareSeries[],
  stamps: readonly number[],
  hidden: ReadonlySet<string> = new Set(),
): (BlendCell | null)[] {
  const members = contributors(series, hidden).map((s) => ({
    id: s.id,
    at: valuesByInstant(s),
  }))
  return stamps.map((stamp) => {
    const ids: string[] = []
    let total = 0
    for (const member of members) {
      const value = member.at.get(stamp)
      if (value === undefined || value === null) continue
      total += value
      ids.push(member.id)
    }
    if (ids.length < MIN_CONTRIBUTORS) return null
    return { value: total / ids.length, n: ids.length, ids }
  })
}

/** One series' usable values, keyed by instant. `below` is a value, not a hole. */
export function valuesByInstant(series: CompareSeries): Map<number, number | null> {
  const out = new Map<number, number | null>()
  for (const point of series.points) {
    const at = Date.parse(point.t)
    if (Number.isNaN(at)) continue
    out.set(at, typeof point.v === 'number' ? point.v : null)
  }
  return out
}
